import * as React from 'react';
import { Case, QueueConfiguration, SubjectImagePreferences, ContentType } from 'services';
import { JsonFrame, GpsddContent, VideoContent, UnsupportedContentType, TextType } from './content';
import { MessageBar, MessageBarType } from '@fluentui/react';
import { AudioContent } from './content/AudioContent';
import { environment } from 'environment';
import { ImageUrlType } from './content/jsonContentTypes/ImageUrlType';

// Attempt to serialize the content
// If we can, render it
// If we can't, output an error message
function renderJsonContent(
  oneCase: boolean | undefined,
  caseData: Case,
  config: QueueConfiguration,
  settings?: SubjectImagePreferences,
  className?: string,
  onClick?: () => void,
  isZoomInActive?: boolean,
  checked?: boolean
): JSX.Element {
  const customCssBaseUrl: string = environment.customCssBaseUrl;
  const hasCustomCss = config.uxPreferences.subjectJson.customCss;
  const isSingleView = config.defaultMaxAssignedCaseCount === 1;
  const cssLink = `${customCssBaseUrl}${config.tenant.tenantId}/${config.title.titleId}_${config.subjectType}.css`;
  const jsonToRender = caseData.subject.subjectValue ? caseData.subject.subjectValue : '';
  const focusText = (caseData.subject.subjectHashes && caseData.subject.subjectHashes['text']) || '';
  const jsonParsed = JSON.parse(jsonToRender);
  return (
    <JsonFrame
      value={jsonParsed}
      focusText={focusText}
      cssLink={hasCustomCss ? cssLink : undefined}
      isSingleView={isSingleView}
      oneCase={oneCase}
      uxPreferences={config.uxPreferences}
      settings={settings}
      className={className || ''}
      onClick={onClick}
      isZoomInActive={isZoomInActive}
      checked={checked}
    />
  );
}

export interface CaseContentProps {
  case: Case;
  config: QueueConfiguration;
  settings?: SubjectImagePreferences;
  className?: string;
  oneCase?: boolean;
  onClick?: () => void;
  isZoomInActive?: boolean;
  checked?: boolean;
}

export const CaseContent: React.FunctionComponent<CaseContentProps> = props => {
  try {
    if (props.config.contentType === ContentType.gpsdd) {
      const position: string[] = props.case.subject.subjectId.split(',');
      return (
        <GpsddContent
          position={position}
          label={(props.case.subject.subjectLabels && props.case.subject.subjectLabels[0]) || ''}
          className={props.className}
          onClick={props.onClick}
        />
      );
    } else if (props.config.contentType === ContentType.image) {
      return (
        <ImageUrlType
          value={props.case.subject.subjectValue || ''}
          subjectImagePreferences={props.settings}
          uxPreferences={props.config.uxPreferences}
          isZoomInActive={props.isZoomInActive}
          isGridView={props.config.defaultMaxAssignedCaseCount === 1 ? false : true}
          caption={''}
        />
      );
    } else if (props.config.contentType === ContentType.video) {
      return (
        <VideoContent
          isGridView={props.config.defaultMaxAssignedCaseCount === 1 ? false : true}
          src={props.case.subject.subjectValue || ''}
          className={props.className}
          onClick={props.onClick}
          frames={props.case.subject.subjectMetadata && props.case.subject.subjectMetadata.frames}
          subjectImagePreferences={props.settings}
          uxPreferences={props.config.uxPreferences}
          isZoomInActive={props.isZoomInActive}
        />
      );
    } else if (props.config.contentType === ContentType.audio) {
      return (
        <AudioContent src={props.case.subject.subjectValue || ''} className={props.className} onClick={props.onClick} />
      );
    } else if (props.config.contentType === ContentType.text) {
      return <TextType setColor={true} val={props.case.subject.subjectValue || ''} settings={props.settings} />;
    } else if (props.config.contentType === ContentType.json) {
      return renderJsonContent(
        props.oneCase,
        props.case,
        props.config,
        props.settings,
        props.className,
        props.onClick,
        props.isZoomInActive,
        props.checked
      );
    } else {
      return <UnsupportedContentType />;
    }
  } catch (error) {
    return (
      <div className={`w-100 p-4 overflow-auto ${props.className || ''}`} onClick={props.onClick}>
        <MessageBar className="mt-4" messageBarType={MessageBarType.error}>
          {(error as Error).toString()}
        </MessageBar>
        <MessageBar className="mt-4" messageBarType={MessageBarType.error}>
          <b>Value:</b> {props.case.subject.subjectValue}
        </MessageBar>
      </div>
    );
  }
};
